<template>
    <div class="pa-4">
        <div class="text-center mb-4">
            <v-icon size="36" color="primary" class="mb-2">{{ headerIcon }}</v-icon>
            <h3 class="text-subtitle-1 font-weight-bold">{{ headerTitle }}</h3>
            <p class="text-body-2 text-medium-emphasis">{{ headerSubtitle }}</p>
        </div>

        <v-text-field v-model="configName"
                      ref="nameField"
                      :label="lockType ? (isSpeed ? 'Speed alert name' : 'Safe zone name') : 'Alert Name'"
                      :placeholder="namePlaceholder"
                      prepend-inner-icon="mdi-label-outline" variant="outlined" class="mb-2"
                      @update:model-value="$emit('setName', configName)"/>

        <!-- Quick name templates for safe zones (lowers setup friction). -->
        <div v-if="isGeofence" class="mb-4">
            <p class="text-caption text-medium-emphasis mb-1">Quick templates</p>
            <v-chip-group>
                <v-chip v-for="t in zoneTemplates" :key="t.label" size="small" variant="outlined"
                        :prepend-icon="t.icon" @click="applyTemplate(t)">{{ t.label }}</v-chip>
            </v-chip-group>
        </div>

        <template v-if="!lockType">
        <p class="text-body-2 font-weight-medium mb-3">What kind of alert?</p>
        <div class="d-flex ga-4 justify-center">
            <v-card :class="{'type-selected': type.toLowerCase() === 'geofence'}"
                    class="type-card text-center pa-5 cursor-pointer flex-grow-1"
                    variant="outlined" rounded="lg" max-width="240"
                    @click="() => { type = 'Geofence'; $emit('setType', type) }">
                <v-icon size="40" :color="type.toLowerCase() === 'geofence' ? 'primary' : 'grey'" class="mb-3">
                    mdi-map-marker-radius
                </v-icon>
                <h3 class="text-subtitle-1 font-weight-bold mb-1">Safe Zone</h3>
                <p class="text-caption text-medium-emphasis mb-0">
                    Get told when a tracker arrives at or leaves a place
                </p>
            </v-card>

            <v-card :class="{'type-selected': type.toLowerCase() === 'speed'}"
                    class="type-card text-center pa-5 cursor-pointer flex-grow-1"
                    variant="outlined" rounded="lg" max-width="240"
                    @click="() => { type = 'Speed'; $emit('setType', type) }">
                <v-icon size="40" :color="type.toLowerCase() === 'speed' ? 'primary' : 'grey'" class="mb-3">
                    mdi-speedometer
                </v-icon>
                <h3 class="text-subtitle-1 font-weight-bold mb-1">Speed Alert</h3>
                <p class="text-caption text-medium-emphasis mb-0">
                    Get told when a tracker goes over a speed limit
                </p>
            </v-card>
        </div>
        </template>
    </div>
</template>

<script setup>
import {computed, nextTick, onMounted, ref, watch} from "vue";

const props = defineProps({
    type: String,
    name: [String, null],
    lockType: {type: Boolean, default: false},
});
const configName = ref(props.name);
const nameField = ref(null);
// Focus the name box when this step is shown (after the stepper transition).
onMounted(() => {
    nextTick(() => setTimeout(() => nameField.value?.focus?.(), 150));
});
const type = ref(props.type ?? 'Geofence');
// On edit the parent resolves the real type asynchronously (after fetch), so
// follow prop changes - otherwise a Speed edit keeps showing the geofence
// name chips/placeholder from the initial 'Geofence' default.
watch(() => props.type, (v) => { if (v) type.value = v; });
const isSpeed = computed(() => (props.type ?? '').toLowerCase() === 'speed');
// Chips reflect the live selection (the local type ref), not just the preset.
const isGeofence = computed(() => (type.value ?? '').toLowerCase() !== 'speed');
// Placeholder examples match the alert kind (speed alerts are not "Home/School").
const namePlaceholder = computed(() => isGeofence.value
    ? 'e.g. Home, School, Work'
    : 'e.g. School Zone, Highway, Neighborhood');

// Common safe-zone names so users do not have to type the obvious ones.
const zoneTemplates = [
    {label: 'Home', icon: 'mdi-home-outline'},
    {label: 'School', icon: 'mdi-school-outline'},
    {label: 'Work', icon: 'mdi-briefcase-outline'},
    {label: 'Gym', icon: 'mdi-dumbbell'},
    {label: 'Office', icon: 'mdi-office-building-outline'},
];
const applyTemplate = (t) => {
    configName.value = t.label;
    emits('setName', t.label);
};
const headerIcon = computed(() => props.lockType
    ? (isSpeed.value ? 'mdi-speedometer' : 'mdi-map-marker-radius')
    : 'mdi-shield-alert-outline');
// No "New" prefix: lockType is now also true when editing, so keep it neutral.
const headerTitle = computed(() => props.lockType
    ? (isSpeed.value ? 'Speed alert' : 'Safe zone')
    : 'Name & Type');
const headerSubtitle = computed(() => props.lockType ? 'Give it a name' : 'Name your alert and pick a type');
const emits = defineEmits(['setName', 'setType']);
</script>

<style scoped>
.type-card {
    transition: all 0.2s ease;
    border: 2px solid transparent !important;
}

.type-card:hover {
    border-color: rgba(var(--v-theme-primary), 0.3) !important;
    transform: translateY(-2px);
}

.type-selected {
    border-color: rgb(var(--v-theme-primary)) !important;
    background: rgba(var(--v-theme-primary), 0.05);
}
</style>
